/**
 * 「전체 상품」 목록의 판정 (v11, `docs/27` §3).
 *
 * **순수 함수로 뗀다** — 이 패키지는 node 환경에서 테스트한다(jsdom 미도입). `document` 나
 * `localStorage` 를 직접 만지면 이 판정이 검사에서 통째로 빠진다 (`channel-selection.ts`·
 * `routing.ts`·`client-type.ts` 와 같은 이유).
 *
 * **목록은 그 채널의 플레이리스트 그대로다** (절대 규칙 34). 여기서 거르거나 다시 정렬하지
 * 않는다 — 재생 가능한 것만 들어오는 것은 서버가 이미 보장했고(절대 규칙 1), 순서는 회전을
 * 포함해 **서버가 준 것이 진실**이다.
 */

import type { PlaylistItem } from '@apm/contracts';

/** 목록 한 줄. 화면이 그리는 데 필요한 것만 담는다 */
export interface ProductListRow {
  productId: string;
  title: string;
  category: string;
  /** 원 단위 정수. 표기는 `@apm/i18n` 의 `formatPrice` 가 한다 */
  price: number;
  thumbnailUrl: string;
  /** 지금 재생 중인 항목. 화면이 `aria-current` 로 표시한다 */
  current: boolean;
  /**
   * 회차 정보 (v16). 홈쇼핑 상품이면 `null`.
   *
   * **행의 내용만 갈리고 시트 셸은 같다** — 시트를 복제하지 않는 것이 `docs/38` §6.4 다.
   */
  content: PlaylistItem['content'];
  /**
   * 콘텐츠 길이(초).
   *
   * **v18 이 `progress` 를 지웠다** — 진행이 회차마다 있지 않고 (계정, 채널) 한 지점이라
   * 행이 그릴 것이 없다. 「이어볼 회차」는 `PlaylistResponse.resume` 이 가리킨다.
   */
  durationSec: number;
}

export type PickOutcome =
  /** 지금 보고 있는 상품이다. **전환하지 않는다** */
  | { kind: 'same' }
  /** 다른 상품이다. `selectItem()` 으로 제2 플레이리스트를 받는다 */
  | { kind: 'switch'; productId: string };

/**
 * 플레이리스트와 현재 상품 → 목록 행.
 *
 * **순서는 받은 그대로다** (`docs/27` D6). `selectItem()` 이후 서버가 고른 상품을 맨 앞에
 * 둔 목록을 주는데, 그것이 제2 플레이리스트의 실체이고 시청자에게는 **「지금부터 이어질
 * 순서」**라 오히려 옳다. 원래 편성 순서를 따로 보관해 되돌리지 않는다 — 두 순서를 들고
 * 있으면 어느 것이 진실인지 화면마다 정해야 한다.
 *
 * **`current` 는 많아야 하나다.** `productId` 는 한 플레이리스트 안에서 유일하다
 * (`Schedule` 의 `@@unique([channelId, productId])` 가 그것을 보장한다). 현재 상품이 목록에
 * 없으면 — 전환 도중이거나 채널이 막 바뀐 순간이다 — 아무 줄에도 표시가 붙지 않는다.
 */
export function toProductRows(
  items: readonly PlaylistItem[],
  currentProductId: string | null,
): ProductListRow[] {
  return items.map((item) => ({
    productId: item.productId,
    title: item.title,
    category: item.meta.category,
    price: item.price,
    thumbnailUrl: item.thumbnailUrl,
    current: currentProductId !== null && item.productId === currentProductId,
    content: item.content,
    durationSec: item.durationSec,
  }));
}

/**
 * 고른 상품이 지금 보는 것인가 (`docs/27` D5).
 *
 * **`selectItem()` 은 같은 상품이어도 새 플레이리스트를 받아 `swapNow` 한다** — 보고 있던
 * 상품이 **처음부터 다시 재생된다.** 시청자가 「이걸 살까」 하고 자기가 보던 것을 누른
 * 결과가 되감기면 그것은 사고다.
 *
 * 채널 시트가 「같은 채널을 고르면 아무 일도 일어나지 않는다 — 판정은 호출부가 한다」로
 * 같은 판단을 이미 했다 (`ChannelSheet` 의 `onSelect` 주석).
 *
 * **`same` 이어도 목록은 닫히고 포커스는 구매 버튼으로 간다.** 고른 사람의 의도는
 * 「이것을 사겠다」이고 그것은 이뤄진다 — 다만 재생을 건드리지 않을 뿐이다.
 */
export function resolvePick(currentProductId: string | null, picked: string): PickOutcome {
  return currentProductId !== null && picked === currentProductId
    ? { kind: 'same' }
    : { kind: 'switch', productId: picked };
}
